import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, of, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { ScheduleCorrectionRequestsService } from 'src/app/services/api/schedule-correction-requests/schedule-correction-requests.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  ScheduleCorrectionRequestItem,
  ScheduleCorrectionRequestListResponse,
  ScheduleCorrectionRequestListSummary,
  ScheduleCorrectionRequestModel,
  ScheduleCorrectionRequestStatus,
  ScheduleCorrectionTeachingSlot,
} from 'src/app/services/models/schedule/schedule-correction-requests.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { getApiErrorMessage } from 'src/utils/http-error-message';

type CorrectionReviewTab = 'pending' | 'approved' | 'rejected' | 'all';
type ViceDeanDisplayGroup = 'pending' | 'approved' | 'rejected' | 'other';

const PENDING_DISPLAY_VALUES = new Set(['pending', 'pending_vice_dean_review', 'รอพิจารณา', 'รอดำเนินการ']);
const APPROVED_DISPLAY_VALUES = new Set([
  'approved', 'vice_dean_approved', 'editing', 'resubmitted_for_approval',
  'waiting_finance_cancellation', 'cancelled',
  'completed', 'finished', 'done', 'อนุมัติ', 'อนุมัติแล้ว', 'เสร็จสิ้น', 'รับรองแล้ว',
]);
const REJECTED_DISPLAY_VALUES = new Set(['rejected', 'vice_dean_rejected', 'ไม่อนุมัติ', 'ปฏิเสธ']);
// Mirrors Disbursement::SendBackService::REASON_PREFIX — stamped on some send-back notes,
// stripped here so the "จากส่งเบิก : " prefix never doubles up.
const DISBURSEMENT_REASON_PREFIX_PATTERN = /^\s*\[จาก flow ส่งเบิก\]\s*/;
const THAI_WEEKDAY_LABELS: Record<number, string> = {
  0: 'วันอาทิตย์',
  1: 'วันจันทร์',
  2: 'วันอังคาร',
  3: 'วันพุธ',
  4: 'วันพฤหัสบดี',
  5: 'วันศุกร์',
  6: 'วันเสาร์',
  7: 'วันอาทิตย์',
};

interface CorrectionReviewRow {
  id: number;
  sequence?: number | null;
  teacherName: string;
  teacherEmail: string;
  academicTerm: string;
  academicYear: string;
  itemCount: number;
  requestedAt: string;
  status: ScheduleCorrectionRequestStatus;
  statusLabel: string;
  statusGroup?: 'pending' | 'approved' | 'rejected' | null;
  raw: ScheduleCorrectionRequestModel;
}

interface CorrectionReviewDataSource {
  data: CorrectionReviewRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-schedule-correction-requests-review',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
  ],
  templateUrl: './schedule-correction-requests-review.component.html',
  styleUrl: './schedule-correction-requests-review.component.scss',
})
export class ScheduleCorrectionRequestsReviewComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;
  @ViewChild('approveDialog') approveDialog?: ConfirmDialogComponent;
  @ViewChild('successDialog') successDialog?: ConfirmDialogComponent;
  @ViewChild('errorDialog') errorDialog?: ConfirmDialogComponent;

  academicTermOptions: string[] = [];
  yearOptions: string[] = [];

  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly correctionRequestsService = inject(ScheduleCorrectionRequestsService);
  private readonly toast = inject(ToastService);

  readonly filterForm = this.fb.group({
    academicTerm: [this.getDefaultAcademicTermName()],
    year: [this.getDefaultBuddhistYear()],
    keywords: [''],
  });
  readonly rejectForm = this.fb.group({
    comment: ['', Validators.required],
  });

  columns: any[] = [];
  datasource = signal<CorrectionReviewDataSource>(this.createDataSource([]));
  tableLoading = signal(true);
  actionLoading = signal(false);
  activeTab = signal<CorrectionReviewTab>('all');
  summary = signal<ScheduleCorrectionRequestListSummary>({
    pending_vice_dean_review: 0,
    vice_dean_approved: 0,
    vice_dean_rejected: 0,
  });
  selectedRequest = signal<ScheduleCorrectionRequestModel | null>(null);
  detailOpen = signal(false);
  approveOpen = signal(false);
  rejectOpen = signal(false);
  errorMessage = signal('');
  successTitle = signal('ดำเนินการสำเร็จ');
  successMessage = signal('');
  pageSizeOptions = [10, 20, 50];

  private currentPage = 1;
  private currentLimit = 50;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private academicTerms: AcademicTermYearModel[] = [];
  private readonly reviewedStatusByRequestId = new Map<number, ScheduleCorrectionRequestStatus>();
  private readonly subscriptions = new Subscription();
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    teacherName: 'teacher_name',
    academicTerm: 'academic_term',
    academicYear: 'academic_term',
    requestedAt: 'requested_at',
    status: 'status',
  };
  ngOnInit(): void {
    this.academicTermOptions = [String(this.filterForm.get('academicTerm')?.value ?? '')].filter(Boolean);
    this.yearOptions = [String(this.filterForm.get('year')?.value ?? '')].filter(Boolean);
    this.initColumns();
    this.listenFilters();
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onTabChange(tab: CorrectionReviewTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadRequests(1);
  }

  onSearch(): void {
    this.loadRequests(1);
  }

  onView(row: CorrectionReviewRow): void {
    this.selectedRequest.set(row.raw);
    this.detailOpen.set(true);
  }

  onCloseDetail(): void {
    if (this.actionLoading()) {
      return;
    }

    this.detailOpen.set(false);
    this.selectedRequest.set(null);
  }

  onOpenApprove(row?: CorrectionReviewRow): void {
    const request = row?.raw ?? this.selectedRequest();
    if (!this.canReview(request)) {
      return;
    }

    this.selectedRequest.set(request);
    this.approveOpen.set(true);
    this.approveDialog?.open();
  }

  onCloseApprove(): void {
    if (this.actionLoading()) {
      return;
    }

    this.approveOpen.set(false);
    this.approveDialog?.close();
  }

  onConfirmApprove(): void {
    const request = this.selectedRequest();
    if (this.actionLoading() || request == null || !this.canReview(request)) {
      return;
    }

    this.actionLoading.set(true);
    this.correctionRequestsService.approveCorrectionRequest(
      request.id,
      null,
    ).pipe(
      finalize(() => this.actionLoading.set(false)),
    ).subscribe({
      next: (updatedRequest) => {
        this.approveDialog?.close();
        this.approveOpen.set(false);
        this.detailOpen.set(false);
        this.selectedRequest.set(null);
        this.successTitle.set('อนุมัติคำขอสำเร็จ');
        const waitingForFinanceCancellation = updatedRequest.status === 'waiting_finance_cancellation'
          || updatedRequest.cancellation_required === true;
        this.successMessage.set(waitingForFinanceCancellation
          ? 'อนุมัติคำขอแล้ว ระบบส่งคำขอให้เจ้าหน้าที่การเงินยกเลิกกลุ่มใบปะหน้า ก่อนเปิดให้แก้ไข'
          : 'อนุมัติคำขอแก้ไขตารางสอนหลังรับรองเรียบร้อยแล้ว');
        this.successDialog?.open();
        this.loadRequests(this.currentPage);
      },
      error: (err) => this.openErrorDialog(err),
    });
  }

  onOpenReject(row?: CorrectionReviewRow): void {
    const request = row?.raw ?? this.selectedRequest();
    if (!this.canReview(request)) {
      return;
    }

    this.selectedRequest.set(request);
    this.rejectForm.reset({ comment: '' });
    this.rejectOpen.set(true);
  }

  onCloseReject(): void {
    if (this.actionLoading()) {
      return;
    }

    this.rejectOpen.set(false);
  }

  onConfirmReject(): void {
    const request = this.selectedRequest();
    if (this.actionLoading() || request == null || !this.canReview(request)) {
      return;
    }

    const comment = String(this.rejectForm.getRawValue().comment ?? '').trim();
    if (this.rejectForm.invalid || comment.length === 0) {
      this.rejectForm.controls.comment.setValue(comment);
      this.rejectForm.markAllAsTouched();
      return;
    }

    this.actionLoading.set(true);
    this.correctionRequestsService.rejectCorrectionRequest(
      request.id,
      comment,
    ).pipe(
      finalize(() => this.actionLoading.set(false)),
    ).subscribe({
      next: (updatedRequest) => {
        if (updatedRequest.status !== 'vice_dean_rejected') {
          this.selectedRequest.set(updatedRequest);
          this.errorMessage.set('ระบบยังไม่เปลี่ยนสถานะคำขอเป็นปฏิเสธ กรุณาลองใหม่อีกครั้ง');
          this.errorDialog?.open();
          this.loadRequests(this.currentPage);
          return;
        }

        this.rememberReviewedStatus(updatedRequest);
        this.applyReviewedRequest(updatedRequest);
        this.rejectOpen.set(false);
        this.detailOpen.set(false);
        this.selectedRequest.set(null);
        this.successTitle.set('ไม่อนุมัติคำขอเรียบร้อยแล้ว');
        this.successMessage.set('บันทึกผลการไม่อนุมัติคำขอแก้ไขเรียบร้อยแล้ว');
        this.successDialog?.open();
        this.loadRequests(this.currentPage);
      },
      error: (err) => this.openErrorDialog(err),
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadRequests(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadRequests(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadRequests(1);
  }

  canReview(request: ScheduleCorrectionRequestModel | null): boolean {
    return request?.status === 'pending_vice_dean_review';
  }

  statusBadgeClass(status?: string | null): string {
    switch (this.statusDisplayGroup(status)) {
      case 'approved':
        return 'schedule-correction-status-approved';
      case 'rejected':
        return 'schedule-correction-status-rejected';
      case 'pending':
        return 'schedule-correction-status-pending';
      case 'other':
        return 'schedule-correction-status-pending';
    }
  }

  statusDisplayLabel(status?: string | null, fallback?: string | null): string {
    if (status === 'waiting_finance_cancellation') {
      return 'รอการเงินยกเลิกกลุ่มใบปะหน้า';
    }

    if (status === 'cancelled') {
      return 'ยกเลิกกลุ่มใบปะหน้าแล้ว';
    }

    switch (this.statusDisplayGroup(status, fallback)) {
      case 'pending':
        return 'รอดำเนินการ';
      case 'approved':
        return 'อนุมัติแล้ว';
      case 'rejected':
        return 'ปฏิเสธ';
      case 'other':
        return fallback == null || fallback === '' ? '-' : fallback;
    }
  }

  private statusDisplayGroup(
    status?: string | null,
    fallback?: string | null,
  ): ViceDeanDisplayGroup {
    const normalizedStatus = String(status ?? '').trim();
    const normalizedFallback = String(fallback ?? '').trim();
    const displayValue = normalizedStatus === '' ? normalizedFallback : normalizedStatus;

    if (PENDING_DISPLAY_VALUES.has(displayValue)) return 'pending';
    if (APPROVED_DISPLAY_VALUES.has(displayValue)) return 'approved';
    if (REJECTED_DISPLAY_VALUES.has(displayValue)) return 'rejected';

    return normalizedFallback === '' ? 'other' : this.statusDisplayGroup(normalizedFallback);
  }

  itemSubjectCode(item: ScheduleCorrectionRequestItem): string {
    return this.stringValue(item.approvable?.subject_code ?? item.metadata?.['subject_code']);
  }

  itemSubjectName(item: ScheduleCorrectionRequestItem): string {
    return this.stringValue(item.approvable?.subject_name ?? item.metadata?.['subject_name']);
  }

  itemTeachingType(item: ScheduleCorrectionRequestItem): string {
    const metadataName = item.metadata?.['teaching_type_name'];
    if (typeof metadataName === 'string' && metadataName.trim() !== '') {
      return metadataName;
    }

    if (item.teaching_type === 'SPC') {
      return 'รายวิชาโครงงาน / วิจัย';
    }

    return item.teaching_type ?? '-';
  }

  workloadSplitLabel(item: ScheduleCorrectionRequestItem): string {
    return item.is_workload_split === true ? 'แบ่งภาระงาน' : 'ไม่แบ่งภาระงาน';
  }

  displayValue(value: unknown): string {
    return this.stringValue(value);
  }

  buildPersonDisplayName(
    academicRank: string | null | undefined,
    fullName: string | null | undefined,
  ): string {
    const parts = [academicRank, fullName]
      .map((value) => String(value ?? '').trim().replace(/\s+/g, ' '))
      .filter((value) => value !== '');
    return parts.length === 0 ? '-' : parts.join(' ');
  }

  teachingSlots(item: ScheduleCorrectionRequestItem): ScheduleCorrectionTeachingSlot[] {
    return [...(item.approvable?.teaching_slots ?? [])].sort((left, right) => (
      this.teachingSlotSortValue(left).localeCompare(this.teachingSlotSortValue(right))
    ));
  }

  teachingSlotLabel(slot: ScheduleCorrectionTeachingSlot): string {
    const day = THAI_WEEKDAY_LABELS[Number(slot.day_of_week)] ?? '-';
    const startTime = this.formatTeachingTime(slot.start_time);
    const endTime = this.formatTeachingTime(slot.end_time);
    const time = startTime === '-' || endTime === '-' ? '-' : `${startTime} - ${endTime} น.`;
    return `${day} ${time} ห้อง ${this.displayValue(slot.room_name)}`;
  }

  newCourseRequestDetail(request: ScheduleCorrectionRequestModel): string {
    return this.displayValue(request.new_course_request_note);
  }

  // Redesign — a send-back (Disbursement::SendBackService) IS a real correction request
  // now; this just names where it came from, never a gate on review actions.
  isFromDisbursement(request?: ScheduleCorrectionRequestModel | null): boolean {
    return request?.metadata?.['source'] === 'disbursement_revision';
  }

  courseReasonDisplay(
    request: ScheduleCorrectionRequestModel,
    item: ScheduleCorrectionRequestItem,
  ): string {
    if (!this.isFromDisbursement(request)) {
      return this.displayValue(item.teacher_note);
    }

    const note = this.stringValue(item.teacher_note);
    const stripped = note.replace(DISBURSEMENT_REASON_PREFIX_PATTERN, '').trim();
    return `จากส่งเบิก : ${this.stringValue(stripped)}`;
  }

  formatDateTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('th-TH', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).format(date);
  }

  formatListDateTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('th-TH', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).format(date);
  }

  formatAcademicYear(value: unknown): string {
    const year = Number(value);
    if (!Number.isFinite(year) || year <= 0) {
      return '-';
    }

    return String(this.toBuddhistYear(year));
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
      },
      {
        prop: 'academicTerm',
        name: 'เทอมการศึกษา',
        sortable: true,
        width: 110,
        minWidth: 100,
      },
      {
        prop: 'academicYear',
        name: 'ปีการศึกษา',
        sortable: true,
        width: 115,
        minWidth: 105,
      },
      {
        prop: 'teacherName',
        name: 'ชื่อ - นามสกุล',
        sortable: true,
        width: 280,
        minWidth: 240,
      },
      {
        prop: 'itemCount',
        name: 'จำนวนรายวิชาที่ขอแก้',
        sortable: false,
        width: 150,
        minWidth: 140,
      },
      {
        prop: 'requestedAt',
        name: 'วันที่ส่งคำขอ',
        sortable: true,
        width: 180,
        minWidth: 170,
      },
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: true,
        cellTemplate: this.statusTemplate,
        width: 130,
        minWidth: 120,
        cellClass: 'text-center schedule-correction-status-cell',
        headerClass: 'text-center schedule-correction-status-header',
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 96,
        minWidth: 88,
        cellClass: 'text-center schedule-correction-action-cell',
        headerClass: 'text-center schedule-correction-action-header',
      },
    ];
  }

  private listenFilters(): void {
    this.subscriptions.add(
      this.filterForm.valueChanges
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
        )
        .subscribe(() => this.loadRequests(1)),
    );
  }

  private loadRequests(page = this.currentPage): void {
    this.currentPage = page;
    this.tableLoading.set(true);
    const selectedTerm = this.selectedAcademicTerm();
    this.correctionRequestsService.getCorrectionRequests({
      status: this.statusForActiveTab(),
      academic_term_id: selectedTerm?.id ?? null,
      keywords: String(this.filterForm.getRawValue().keywords ?? '').trim() || null,
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => this.applyListResponse(response),
      error: (err) => {
        this.toast.toastError(this.extractErrorMessage(err));
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private applyListResponse(response: ScheduleCorrectionRequestListResponse): void {
    const activeStatus = this.statusForActiveTab();
    const normalizedRequests = response.data
      .map((request) => this.withLocallyReviewedStatus(request))
      .filter((request) => this.matchesActiveStatus(request.status, activeStatus));
    const removedStaleRows = response.data.length - normalizedRequests.length;

    this.datasource.set(this.createDataSource(
      normalizedRequests.map((request) => this.mapRequestToRow(request)),
      response.page,
      response.limit,
      Math.max(response.total - removedStaleRows, 0),
      response.total_pages,
    ));
    this.summary.set(this.withLocallyReviewedSummary(response.summary, response.data));
  }

  private defaultSummary(): ScheduleCorrectionRequestListSummary {
    return {
      pending_vice_dean_review: 0,
      vice_dean_approved: 0,
      vice_dean_rejected: 0,
    };
  }

  private withLocallyReviewedSummary(
    summary: ScheduleCorrectionRequestListSummary | undefined,
    requests: ScheduleCorrectionRequestModel[],
  ): ScheduleCorrectionRequestListSummary {
    const nextSummary = { ...(summary ?? this.defaultSummary()) };

    requests.forEach((request) => {
      const reviewedStatus = this.reviewedStatusByRequestId.get(request.id);
      if (reviewedStatus == null || reviewedStatus === request.status) {
        return;
      }

      this.incrementSummary(nextSummary, request.status, -1);
      this.incrementSummary(nextSummary, reviewedStatus, 1);
    });

    return nextSummary;
  }

  private incrementSummary(
    summary: ScheduleCorrectionRequestListSummary,
    status: ScheduleCorrectionRequestStatus,
    delta: number,
  ): void {
    const key = this.summaryKeyForStatus(status);
    if (key == null) {
      return;
    }

    summary[key] = Math.max(summary[key] + delta, 0);
  }

  private summaryKeyForStatus(
    status: ScheduleCorrectionRequestStatus,
  ): keyof ScheduleCorrectionRequestListSummary | null {
    switch (status) {
      case 'pending_vice_dean_review':
        return 'pending_vice_dean_review';
      case 'approved':
      case 'vice_dean_approved':
      case 'editing':
      case 'resubmitted_for_approval':
      case 'completed':
      case 'finished':
      case 'done':
      case 'waiting_finance_cancellation':
      case 'cancelled':
        return 'vice_dean_approved';
      case 'rejected':
      case 'vice_dean_rejected':
        return 'vice_dean_rejected';
      case 'pending':
        return null;
    }
  }

  private rememberReviewedStatus(request: ScheduleCorrectionRequestModel): void {
    if (request.status === 'vice_dean_approved' || request.status === 'vice_dean_rejected') {
      this.reviewedStatusByRequestId.set(request.id, request.status);
    }
  }

  private withLocallyReviewedStatus(
    request: ScheduleCorrectionRequestModel,
  ): ScheduleCorrectionRequestModel {
    const reviewedStatus = this.reviewedStatusByRequestId.get(request.id);

    if (reviewedStatus == null || request.status === reviewedStatus) {
      return request;
    }

    return {
      ...request,
      status: reviewedStatus,
      status_label: null,
    };
  }

  private applyReviewedRequest(updatedRequest: ScheduleCorrectionRequestModel): void {
    const activeStatus = this.statusForActiveTab();
    const currentRows = this.datasource().data;
    const nextRows = currentRows
      .map((row) => (row.id === updatedRequest.id ? this.mapRequestToRow(updatedRequest) : row))
      .filter((row) => this.matchesActiveStatus(row.status, activeStatus));
    const removedRows = currentRows.length - nextRows.length;

    if (removedRows > 0 || nextRows.some((row) => row.id === updatedRequest.id)) {
      this.datasource.set(this.createDataSource(
        nextRows,
        this.datasource().page,
        this.datasource().limit,
        Math.max(this.datasource().total - removedRows, 0),
        this.datasource().total_page,
      ));
    }
  }

  private statusForActiveTab(): string | null {
    switch (this.activeTab()) {
      case 'pending':
        return 'pending_vice_dean_review';
      case 'approved':
        return 'vice_dean_approved';
      case 'rejected':
        return 'vice_dean_rejected';
      case 'all':
      default:
        return null;
    }
  }

  private matchesActiveStatus(
    status: string | null | undefined,
    activeStatus: string | null,
  ): boolean {
    if (activeStatus == null) {
      return true;
    }

    return this.statusDisplayGroup(status) === this.statusDisplayGroup(activeStatus);
  }

  private mapRequestToRow(request: ScheduleCorrectionRequestModel): CorrectionReviewRow {
    const term = request.schedule_submission?.academic_term;
    const teacher = request.schedule_submission?.teacher ?? request.requested_by;

    return {
      id: request.id,
      sequence: request.sequence ?? null,
      teacherName: this.buildPersonDisplayName(teacher?.academic_rank, teacher?.full_name),
      teacherEmail: request.schedule_submission?.teacher?.email ?? request.requested_by?.email ?? '-',
      academicTerm: this.displayValue(term?.name),
      academicYear: this.formatAcademicYear(term?.year),
      itemCount: request.items?.length ?? 0,
      requestedAt: this.formatListDateTime(request.requested_at),
      status: request.status,
      statusLabel: this.statusDisplayLabel(
        request.status,
        request.vice_dean_display_status ?? request.status_label,
      ),
      statusGroup: request.vice_dean_status_group,
      raw: request,
    };
  }

  private createDataSource(
    rows: CorrectionReviewRow[],
    page = 1,
    limit = 50,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
  ): CorrectionReviewDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private toBuddhistYear(year: number): number {
    return year < 2400 ? year + 543 : year;
  }

  private loadFilterOptions(): void {
    this.subscriptions.add(
      forkJoin({
        academicTerms: this.studyPeriodService.getAcademicTermYears().pipe(
          take(1),
          catchError((err) => {
            console.error(err);
            return of([] as AcademicTermYearModel[]);
          }),
        ),
      }).subscribe(({ academicTerms }) => {
        this.academicTerms = academicTerms;
        const academicTermOptions = this.buildAcademicTermOptions(academicTerms);
        const yearOptions = this.buildAcademicYearOptions(academicTerms);

        this.academicTermOptions = academicTermOptions.length > 0
          ? academicTermOptions
          : this.academicTermOptions;
        this.yearOptions = yearOptions.length > 0
          ? yearOptions
          : this.yearOptions;
        this.ensureAcademicTermFilterValues();
        this.loadRequests(1);
      }),
    );
  }

  private selectedAcademicTerm(): AcademicTermYearModel | undefined {
    const value = this.filterForm.getRawValue();
    return this.findAcademicTerm(
      String(value.academicTerm ?? ''),
      String(value.year ?? ''),
    );
  }

  teacherDisplayName(request: ScheduleCorrectionRequestModel): string {
    const teacher = request.schedule_submission?.teacher ?? request.requested_by;
    if (teacher == null) {
      return '-';
    }

    const displayName = String(teacher.teacher_display_name ?? '').trim();
    if (displayName !== '') {
      return displayName;
    }

    const academicRank = String(teacher.academic_rank ?? '').trim();
    const fullName = String(teacher.full_name).trim();

    return [academicRank, fullName].filter(Boolean).join(' ') || '-';
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === String(academicTermName) &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private ensureAcademicTermFilterValues(): void {
    const currentAcademicTerm = String(this.filterForm.get('academicTerm')?.value ?? '');
    const currentYear = String(this.filterForm.get('year')?.value ?? '');

    if (this.findAcademicTerm(currentAcademicTerm, currentYear) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.filterForm.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private stringValue(value: unknown): string {
    return typeof value === 'string' && value.trim() !== '' ? value : '-';
  }

  private teachingSlotSortValue(slot: ScheduleCorrectionTeachingSlot): string {
    const day = Number.isFinite(Number(slot.day_of_week)) ? Number(slot.day_of_week) : 99;
    return `${String(day).padStart(2, '0')}:${slot.start_time ?? ''}:${slot.end_time ?? ''}`;
  }

  private formatTeachingTime(value?: string | null): string {
    const match = String(value ?? '').match(/(?:T|\s|^)(\d{2}:\d{2})/);
    return match?.[1] ?? '-';
  }

  private extractErrorMessage(err: unknown): string {
    return getApiErrorMessage(err);
  }

  private openErrorDialog(error: unknown): void {
    this.errorMessage.set(this.extractErrorMessage(error));
    this.errorDialog?.open();
  }
}
